import { useState, useMemo, useEffect } from 'react';
import { Typography, Link, Stack, Alert, Button, Box, Collapse, IconButton } from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { ExpandMore } from '@mui/icons-material';
import LoginForm from '../components/auth/LoginForm';
import OAuthButtons from '../components/auth/OAuthButtons';
import AuthLayout from '../components/auth/AuthLayout';
import { useLang } from '../contexts/langContext';
import { useBranding } from '../contexts/BrandingContext';

const oauthErrorMessages: Record<string, { en: string; zh: string }> = {
  account_exists_different_auth: {
    en: 'This account was registered with a different login method. Please use your original login method.',
    zh: '该账号使用了其他登录方式注册，请使用原登录方式登录。',
  },
  invalid_state: {
    en: 'Login session expired. Please try again.',
    zh: '登录会话已过期，请重试。',
  },
  invalid_resource: {
    en: 'Failed to retrieve account information. Please try again.',
    zh: '获取账号信息失败，请重试。',
  },
  oauth_failed: {
    en: 'Login failed. Please try again.',
    zh: '登录失败，请重试。',
  },
};

function LoginPage() {
  const { lang } = useLang();
  const { oauthButtons, forgotPasswordURL, localLoginForm } = useBranding();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [adminFormExpanded, setAdminFormExpanded] = useState(false);

  const oauthError = searchParams.get('error');
  const oauthErrorMessage =
    oauthError && oauthErrorMessages[oauthError] ? (lang === 'en' ? oauthErrorMessages[oauthError].en : oauthErrorMessages[oauthError].zh) : null;

  const { loginFormLocked, adminLoginFormLocked, displayMessage, redirectUrl } = useMemo(() => {
    const config = localLoginForm;
    if (!config) {
      return { loginFormLocked: false, adminLoginFormLocked: false, displayMessage: undefined, redirectUrl: undefined };
    }

    const isAdminRole = searchParams.get('role') === 'Admin';
    const defaultLocked = config.locked ?? false;
    const adminLocked = config.admin?.locked ?? defaultLocked;

    if (isAdminRole && config.admin !== undefined) {
      // For admin users, use admin settings if specified
      const message = config.admin.message ?? config.message;
      const localizedMessage = lang === 'zh-HK' ? message?.['zh-CN'] : message?.[lang as 'en' | 'zh-CN'];
      return {
        loginFormLocked: adminLocked,
        adminLoginFormLocked: adminLocked,
        displayMessage: localizedMessage,
        redirectUrl: config.redirectUrl,
      };
    }

    // For non-admin users, use default settings
    const localizedMessage = lang === 'zh-HK' ? config.message?.['zh-CN'] : config.message?.[lang as 'en' | 'zh-CN'];
    return {
      loginFormLocked: defaultLocked,
      adminLoginFormLocked: adminLocked,
      displayMessage: localizedMessage,
      redirectUrl: config.redirectUrl,
    };
  }, [localLoginForm, searchParams, lang]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const email = searchParams.get('email');
    const redirect = searchParams.get('redirect');
    if (!email && !redirect) return;

    const params = new URLSearchParams();
    if (email) params.set('email', email);
    if (redirect) params.set('redirect', redirect);
    navigate(`/e2e/login?${params.toString()}`, { replace: true });
  }, [navigate, searchParams]);

  return (
    <AuthLayout title={lang === 'en' ? 'Log In' : '登录'}>
      {/* OAuth error message */}
      {oauthErrorMessage && (
        <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
          {oauthErrorMessage}
        </Alert>
      )}

      {/* Lock message with redirect */}
      {loginFormLocked && displayMessage && (
        <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
          {displayMessage}
          {redirectUrl && (
            <span>
              {' '}
              <Link href={redirectUrl} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 500 }}>
                {lang === 'en' ? 'Click here to continue.' : '点击此处继续'}
              </Link>
            </span>
          )}
        </Alert>
      )}

      {/* Collapsible admin login toggle (when regular login locked but admin allowed) */}
      {loginFormLocked && !adminLoginFormLocked && (
        <Box sx={{ mt: 2, borderTop: 1, borderColor: 'divider', pt: 2 }}>
          <Button
            onClick={() => setAdminFormExpanded(!adminFormExpanded)}
            fullWidth
            variant="text"
            size="small"
            endIcon={
              <ExpandMore
                sx={{
                  transform: adminFormExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            }
            sx={{
              justifyContent: 'space-between',
              color: 'text.secondary',
              textTransform: 'none',
              fontSize: '0.75rem',
            }}
          >
            {lang === 'en' ? 'Admin Login' : '管理员登录'}
          </Button>
          <Collapse in={adminFormExpanded}>
            <Box sx={{ mt: 1 }}>
              <LoginForm />
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Show form when: not locked, OR (locked but admin allowed AND expanded) */}
      {!loginFormLocked && <LoginForm />}
      <Stack spacing={2} sx={{ mt: 2 }}>
        {!loginFormLocked && (
          <Stack sx={{ textAlign: 'center', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {lang === 'en' ? "Don't have an account?" : '没有账号？'}{' '}
              <Button
                size="small"
                variant="text"
                component={RouterLink}
                to="/signup"
                sx={{
                  verticalAlign: 'baseline',
                  color: 'primary.main',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {lang === 'en' ? 'Sign up' : '注册'}
              </Button>
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              <Button
                size="small"
                variant="text"
                component={RouterLink}
                to="/forgot-password"
                sx={{
                  verticalAlign: 'baseline',
                  color: 'primary.main',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                {lang === 'en' ? 'Forgot password?' : '忘记密码？'}
              </Button>
            </Typography>
          </Stack>
        )}
        {forgotPasswordURL && (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem', textAlign: 'center' }}>
            <Link
              href={forgotPasswordURL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {lang === 'en' ? 'External password reset' : '外部密码重置'}
            </Link>
          </Typography>
        )}
      </Stack>
      <Box sx={{ mt: 2 }}>
        <OAuthButtons oauthButtons={oauthButtons} />
      </Box>
    </AuthLayout>
  );
}

export default LoginPage;
